import React, { useState, useEffect } from 'react';
import { Shield, Save, Check } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { getRolePermissions, updateRolePermissions } from '../api';

const ROLE_LABELS: Record<string, string> = { hr: 'HR', vip: 'VIP面试官', interviewer: '面试官', referrer: '内推官', it: 'IT资源官' };
const ROLE_ORDER = ['hr', 'vip', 'interviewer', 'it', 'referrer'];

interface ModuleDef { key: string; label: string; }
interface EventDef { key: string; label: string; desc?: string; }
interface ScopeDef { key: string; label: string; }
interface RoleConf { modules: string[]; position_scope: 'all' | 'specific'; notify_events: string[]; approval_scopes: string[]; }

export default function PermissionManager() {
  const [modules, setModules] = useState<ModuleDef[]>([]);
  const [notifyEvents, setNotifyEvents] = useState<EventDef[]>([]);
  const [approvalScopes, setApprovalScopes] = useState<ScopeDef[]>([]);
  const [roles, setRoles] = useState<Record<string, RoleConf>>({});
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState<string | null>(null);
  const [savedRole, setSavedRole] = useState<string | null>(null);

  const load = async () => {
    try {
      const data = await getRolePermissions();
      setModules(data.modules || []);
      setNotifyEvents(data.notify_events || []);
      setApprovalScopes(data.approval_scopes || []);
      setRoles(data.roles || {});
    } catch { toast.error('获取角色权限失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleInList = (role: string, field: 'modules' | 'notify_events' | 'approval_scopes', key: string) => {
    setRoles(prev => {
      const cur = prev[role] || { modules: [], position_scope: 'specific', notify_events: [], approval_scopes: [] };
      const list = cur[field] || [];
      const has = list.includes(key);
      return { ...prev, [role]: { ...cur, [field]: has ? list.filter(m => m !== key) : [...list, key] } };
    });
  };

  const setScope = (role: string, scope: 'all' | 'specific') => {
    setRoles(prev => ({ ...prev, [role]: { ...(prev[role] || { modules: [], notify_events: [], approval_scopes: [] }), position_scope: scope } }));
  };

  const handleSave = async (role: string) => {
    setSavingRole(role);
    try {
      const conf = roles[role];
      await updateRolePermissions(role, { modules: conf.modules, position_scope: conf.position_scope, notify_events: conf.notify_events, approval_scopes: conf.approval_scopes });
      toast.success(`${ROLE_LABELS[role]} 权限已保存`);
      setSavedRole(role);
      setTimeout(() => setSavedRole(null), 2000);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '保存失败');
    } finally { setSavingRole(null); }
  };

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">加载中...</div>;

  const editableRoles = ROLE_ORDER.filter(r => roles[r]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-gray-700">
        <Shield size={16} className="text-primary-500" />
        <span className="text-sm font-medium">角色权限模板</span>
      </div>
      <p className="text-[11px] text-gray-400 leading-relaxed">
        设置每个角色默认可见的功能模块、岗位查看范围、飞书通知订阅和审批权限。修改后对该角色的所有成员生效（已单独覆盖的成员除外）。管理员拥有全部权限，不可修改。
      </p>

      {editableRoles.map(role => {
        const conf = roles[role];
        return (
          <div key={role} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-800">{ROLE_LABELS[role]}</span>
              <button
                onClick={() => handleSave(role)}
                disabled={savingRole === role}
                className={clsx(
                  'flex items-center gap-1 px-2.5 h-7 rounded-lg text-[11px] font-medium transition-colors',
                  savedRole === role ? 'bg-green-50 text-green-600' : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                )}
              >
                {savedRole === role ? <><Check size={12} />已保存</> : <><Save size={12} />{savingRole === role ? '保存中' : '保存'}</>}
              </button>
            </div>

            {/* Position scope */}
            <div>
              <div className="text-[11px] text-gray-500 mb-1.5">岗位查看范围</div>
              <div className="flex gap-1.5">
                {(['all', 'specific'] as const).map(s => (
                  <button key={s}
                    onClick={() => setScope(role, s)}
                    className={clsx(
                      'px-3 py-1 rounded-lg text-[11px] border transition-colors',
                      conf.position_scope === s ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                    )}
                  >
                    {s === 'all' ? '全部岗位' : '指定岗位'}
                  </button>
                ))}
              </div>
            </div>

            {/* Module checkboxes */}
            <div>
              <div className="text-[11px] text-gray-500 mb-1.5">可见功能模块</div>
              <div className="flex flex-wrap gap-1.5">
                {modules.map(m => {
                  const on = conf.modules.includes(m.key);
                  return (
                    <button key={m.key}
                      onClick={() => toggleInList(role, 'modules', m.key)}
                      className={clsx(
                        'px-2 py-1 rounded-md text-[11px] border transition-colors',
                        on ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                      )}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Feishu notification subscriptions */}
            <div>
              <div className="text-[11px] text-gray-500 mb-1.5">飞书通知订阅</div>
              <div className="flex flex-wrap gap-1.5">
                {notifyEvents.map(e => {
                  const on = (conf.notify_events || []).includes(e.key);
                  return (
                    <button key={e.key}
                      title={e.desc || ''}
                      onClick={() => toggleInList(role, 'notify_events', e.key)}
                      className={clsx(
                        'px-2 py-1 rounded-md text-[11px] border transition-colors',
                        on ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                      )}
                    >
                      {e.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Approval scopes */}
            <div>
              <div className="text-[11px] text-gray-500 mb-1.5">审批权限</div>
              <div className="flex flex-wrap gap-1.5">
                {approvalScopes.map(s => {
                  const on = (conf.approval_scopes || []).includes(s.key);
                  return (
                    <button key={s.key}
                      onClick={() => toggleInList(role, 'approval_scopes', s.key)}
                      className={clsx(
                        'px-2 py-1 rounded-md text-[11px] border transition-colors',
                        on ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                      )}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
